import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchApplicationFormSharePointSettings,
  saveCffSharePointSettings,
  testApplicationFormSharePointConnection,
  type ApplicationFormSharePointSettings,
} from '../../../lib/applicationFormSettings';
import { formatSupabaseError } from '../../../lib/formatSupabaseError';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { formatSingaporeDateTime } from '../../../utils/dateTime';
import { Modal } from '../../ui/Modal';
import { SharePointSyncToggle } from '../../settings/SharePointSyncToggle';

function formatCffSiteInput(row: ApplicationFormSharePointSettings): string {
  const host = row.cff_site_hostname || '';
  if (!host) return '';
  return `https://${host}${row.cff_site_path || ''}`;
}

function parseSiteInput(input: string): { site_hostname: string; site_path: string } {
  const trimmed = input.trim();
  if (!trimmed) return { site_hostname: '', site_path: '' };
  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    return { site_hostname: url.hostname, site_path: url.pathname.replace(/\/$/, '') || '' };
  } catch {
    const parts = trimmed.replace(/^https?:\/\//, '').split('/');
    const host = parts[0] ?? '';
    const path = parts.length > 1 ? `/${parts.slice(1).join('/')}` : '';
    return { site_hostname: host, site_path: path.replace(/\/$/, '') };
  }
}

function formatStoredError(raw?: string | null): string {
  if (!raw) return '—';
  if (raw === '[object Object]') {
    return 'Previous sync failed. Save a CFF or use Export PDF to retry.';
  }
  return raw;
}

/** Customer Feedback Form SharePoint — own site/folder, like Sales CRM vs IT. */
export function CustomerFeedbackSharePointPanel({
  embeddedInAdminHub = false,
}: {
  embeddedInAdminHub?: boolean;
}) {
  void embeddedInAdminHub;

  const [settings, setSettings] = useState<ApplicationFormSharePointSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editOpenRef = useRef(false);
  editOpenRef.current = editOpen;

  const [enabled, setEnabled] = useState(false);
  const [siteInput, setSiteInput] = useState('');
  const [folderPath, setFolderPath] = useState('Customer Feedback Form');

  const applyRow = useCallback((row: ApplicationFormSharePointSettings) => {
    setEnabled(Boolean(row.cff_enabled));
    if (!editOpenRef.current) {
      setSiteInput(formatCffSiteInput(row));
      setFolderPath(row.cff_folder_path || 'Customer Feedback Form');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await fetchApplicationFormSharePointSettings();
      setSettings(row);
      if (row) applyRow(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : formatSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }, [applyRow]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['sharepoint_settings'],
    onChange: () => void load(),
  });

  function openEdit() {
    if (settings) {
      setEnabled(Boolean(settings.cff_enabled));
      setSiteInput(formatCffSiteInput(settings));
      setFolderPath(settings.cff_folder_path || 'Customer Feedback Form');
    }
    setError(null);
    setMessage(null);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    if (settings) applyRow(settings);
    setError(null);
    setMessage(null);
  }

  async function handleToggleEnabled(next: boolean) {
    if (!settings?.cff_site_hostname) {
      setError('Set site URL and folder in Edit before enabling sync.');
      return;
    }
    setToggling(true);
    setError(null);
    setMessage(null);
    setEnabled(next);
    try {
      const saved = await saveCffSharePointSettings({
        enabled: next,
        site_hostname: settings.cff_site_hostname,
        site_path: settings.cff_site_path || '',
        folder_path: settings.cff_folder_path || 'Customer Feedback Form',
      });
      setSettings(saved);
      setMessage(next ? 'Sync turned on.' : 'Sync turned off.');
    } catch (err) {
      setEnabled(!next);
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setToggling(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const parsed = parseSiteInput(siteInput);
    if (!parsed.site_hostname || !parsed.site_path) {
      setError('Enter a valid SharePoint site URL (e.g. https://banteckhansg.sharepoint.com/sites/BanTeckHan).');
      return;
    }
    if (!folderPath.trim()) {
      setError('Enter a CFF folder path inside Documents.');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveCffSharePointSettings({
        enabled,
        site_hostname: parsed.site_hostname,
        site_path: parsed.site_path,
        folder_path: folderPath.trim(),
      });
      setSettings(saved);
      setMessage('CFF SharePoint settings saved.');
      setEditOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    const source = editOpen
      ? { site: siteInput, folder: folderPath }
      : {
          site: settings ? formatCffSiteInput(settings) : siteInput,
          folder: settings?.cff_folder_path || folderPath,
        };
    const parsed = parseSiteInput(source.site);
    if (!parsed.site_hostname || !parsed.site_path) {
      setError('Enter a valid SharePoint site URL before testing.');
      return;
    }
    if (!source.folder.trim()) {
      setError('Enter a folder path before testing.');
      return;
    }

    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await testApplicationFormSharePointConnection({
        target: 'cff',
        site_hostname: parsed.site_hostname,
        site_path: parsed.site_path,
        folder_path: source.folder.trim(),
      });
      setMessage(
        `Connected to ${String(result.siteName ?? 'SharePoint site')}${
          result.folderUrl ? ' · folder OK' : ''
        }.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  }

  const busy = loading || saving || testing || toggling;
  const siteDisplay = settings ? formatCffSiteInput(settings) : '';

  return (
    <>
      <article className="sharepoint-config-row">
        <div className="sharepoint-config-row__main">
          <div className="sharepoint-config-row__identity">
            <h3 className="sharepoint-config-row__title">Customer Feedback</h3>
            {!loading ? (
              <p className="sharepoint-config-row__meta" title={siteDisplay || undefined}>
                {siteDisplay || 'No site configured'}
                {settings?.cff_folder_path ? ` · ${settings.cff_folder_path}` : ''}
              </p>
            ) : (
              <p className="sharepoint-config-row__meta">Loading…</p>
            )}
          </div>

          <div className="sharepoint-config-row__status">
            <div>
              <span className="sharepoint-config-row__status-label">Last sync</span>
              <span className="sharepoint-config-row__status-value">
                {settings?.cff_last_sync_at
                  ? formatSingaporeDateTime(settings.cff_last_sync_at)
                  : '—'}
              </span>
            </div>
            <div className="sharepoint-config-row__status-wide">
              <span className="sharepoint-config-row__status-label">
                {settings?.cff_last_sync_error && !editOpen ? 'Error' : 'Folder'}
              </span>
              {settings?.cff_last_sync_error && !editOpen ? (
                <span
                  className="sharepoint-config-row__status-value sharepoint-config-row__status-value--error"
                  title={formatStoredError(settings.cff_last_sync_error)}
                >
                  {formatStoredError(settings.cff_last_sync_error)}
                </span>
              ) : (
                <span
                  className="sharepoint-config-row__status-value"
                  title={settings?.cff_folder_path || undefined}
                >
                  {settings?.cff_folder_path || '—'}
                </span>
              )}
            </div>
          </div>
        </div>

        {!editOpen && error ? <div className="alert alert-error sharepoint-config-row__alert">{error}</div> : null}
        {!editOpen && message ? (
          <div className="alert alert-success sharepoint-config-row__alert">{message}</div>
        ) : null}

        <div className="sharepoint-config-row__actions">
          <SharePointSyncToggle
            variant="compact"
            checked={enabled}
            disabled={busy}
            onChange={(next) => void handleToggleEnabled(next)}
          />
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleTest()}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={openEdit}>
            Edit
          </button>
        </div>
      </article>

      <Modal title="Edit · Customer Feedback" open={editOpen} onClose={closeEdit} wide>
        <form
          className="settings-sharepoint-form settings-sharepoint-form--modal"
          onSubmit={(e) => void handleSave(e)}
        >
          {error ? <div className="alert alert-error">{error}</div> : null}
          {message ? <div className="alert alert-success">{message}</div> : null}

          <div className="settings-sharepoint-form__fields">
            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Site URL</span>
              <input
                className="field-input"
                placeholder="https://banteckhansg.sharepoint.com/sites/BanTeckHan"
                value={siteInput}
                onChange={(e) => setSiteInput(e.target.value)}
                disabled={saving}
              />
            </label>

            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Folder path</span>
              <input
                className="field-input"
                placeholder="Customer Feedback Form"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
          <p className="field-hint settings-sharepoint-form__hint">
            Monthly workbook like CRM: <code>08. BTH Portal - CFF Form - Aug&apos;26.xlsx</code>.
            Each running no is a sheet named <code>001</code>, <code>002</code>, …
          </p>

          <div className="settings-sharepoint-form__actions">
            <button type="submit" className="btn-primary" disabled={saving || testing}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || testing}
              onClick={() => void handleTest()}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button type="button" className="btn-secondary" disabled={saving} onClick={closeEdit}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
